import { captureException } from '@sentry/react'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import {
  ReferenceMode,
  TabKey
} from 'containers/SubSidebar/AIVideoContent/consts'
import { withAppThunks } from 'store/with-app-thunks'
import { TAppDispatch, TGetState } from 'store/types'
import { IAnimateImageParams } from 'store/types/aiVideo'
import { getDeviceVersion } from 'utils'
import { ReduxThunkError } from 'utils/errors'
import { IAiImageItemDetails, IAIImageProject } from 'store/types/aiImage'
import slice from 'store/features/aiVideo/slice'
import { getClosestPossibleVideoDuration } from './utils'
import { DEFAULT_MEDIA_DURATION } from 'consts'
import { createReferenceMediaItem } from '../utils'
import { ReferenceMediaImageType } from 'types/aiVideo'
const { actions } = slice

const setAnimationRequestParams = (params: IAnimateImageParams) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const {
      filePath,
      prompt,
      mediaItemId,
      assetId,
      modelCode,
      isAiImage,
      duration = DEFAULT_MEDIA_DURATION
    } = params
    const defaultSettings = getState().aiVideo.defaultGenerationSettings
    const selectedModelData = getState().aiVideo.aiVideoModels.find(
      model => model.model === (modelCode || defaultSettings?.selectedModel)
    )
    const closestDuration = getClosestPossibleVideoDuration(
      duration,
      selectedModelData
    )

    dispatch(appThunks.aiVideoThunks.clearGenerationParams())
    dispatch(appThunks.aiVideoThunks.setIsConsistencyEnabled(false))

    if (assetId && isAiImage) {
      dispatch(appThunks.aiVideoThunks.setAiImageId(Number(assetId)))
    }

    dispatch(appThunks.aiVideoThunks.setReferenceMode(ReferenceMode.FRAMES))
    if (filePath) {
      dispatch(
        appThunks.aiVideoThunks.addReferenceMediaItem({
          item: createReferenceMediaItem(
            ReferenceMediaImageType.FIRST_FRAME,
            filePath
          ),
          bucket: ReferenceMode.FRAMES
        })
      )
    }
    if (prompt) {
      dispatch(appThunks.aiVideoThunks.setPrompt(prompt))
    }

    if (duration) {
      dispatch(appThunks.aiVideoThunks.setDuration(closestDuration))
    }
    if (mediaItemId) {
      dispatch(appThunks.aiVideoThunks.setTargetMediaItemId(mediaItemId))
    }
    if (modelCode) {
      dispatch(
        appThunks.aiVideoThunks.updateModelCreditsPerDuration({
          modelCode,
          duration: closestDuration
        })
      )
    } else {
      if (defaultSettings?.selectedModel) {
        dispatch(
          appThunks.aiVideoThunks.updateSelectedModel({
            modelName: defaultSettings.selectedModel,
            duration: closestDuration || defaultSettings.duration || undefined,
            quality: defaultSettings.quality || undefined,
            aspectRatio: defaultSettings.aspectRatio || undefined
          })
        )
      }
    }
  })

const getAiImageDataByAssetId = (assetId: number) =>
  withAppThunks(appThunks => async (dispatch: TAppDispatch) => {
    const response = await dispatch(
      appThunks.aiImageThunks.getAiImageByAssetId(Number(assetId))
    )
    if (response) {
      const {
        images: [{ filePath }],
        meta
      } = response as IAIImageProject

      const data: IAiImageItemDetails = {
        filePath,
        meta
      }

      return data
    }
  })

const animateImage = (params: IAnimateImageParams) =>
  withAppThunks(appThunks => async (dispatch: TAppDispatch) => {
    const { filePath } = params
    const isMobile = getDeviceVersion()

    if (filePath) {
      dispatch(
        appThunks.editorThunks.setActiveSidebarTabAndCloseOverlay(
          SUB_SIDEBAR_KEYS.AI_VIDEO
        )
      )
      dispatch(appThunks.aiVideoThunks.setActiveTab(TabKey.GENERATE))
      dispatch(setAnimationRequestParams(params))
      if (isMobile) {
        dispatch(appThunks.editorThunks.setIsMobileSubSidebarOpen(true))
      }
    }
  })

const animateAiImage =
  (params: IAnimateImageParams) => async (dispatch: TAppDispatch) => {
    try {
      const { assetId, mediaItemId, duration = DEFAULT_MEDIA_DURATION } = params
      const data = (await dispatch(
        getAiImageDataByAssetId(Number(assetId))
      )) as IAiImageItemDetails
      if (!data) {
        return
      }
      const { filePath, meta } = data

      dispatch(
        animateImage({
          filePath,
          prompt: meta?.animationPrompt,
          mediaItemId,
          assetId,
          isAiImage: true,
          duration
        })
      )
    } catch (error) {
      captureException(new ReduxThunkError(error))
    }
  }

const instantImageAnimation = (params: IAnimateImageParams) =>
  withAppThunks(appThunks => async (dispatch: TAppDispatch) => {
    let data: IAnimateImageParams
    if (params.isAiImage && params.assetId) {
      const imageDetails = await dispatch(
        getAiImageDataByAssetId(Number(params.assetId))
      )
      if (!imageDetails) {
        return
      }
      const { filePath, meta } = imageDetails as IAiImageItemDetails
      data = {
        filePath,
        prompt: meta?.animationPrompt,
        mediaItemId: params.mediaItemId,
        assetId: Number(params.assetId),
        modelCode: params?.modelCode,
        isAiImage: params.isAiImage,
        duration: params.duration
      }
    } else {
      data = params
    }

    if (!data || !data?.mediaItemId || !data?.filePath) {
      return
    }

    dispatch(actions.setTargetMediaItemId(data.mediaItemId))
    dispatch(appThunks.editorThunks.setAsideOverlayContent(null))
    dispatch(
      appThunks.editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_VIDEO)
    )
    dispatch(setAnimationRequestParams(data))
    dispatch(appThunks.aiVideoThunks.generateAiVideo())
  })

export const animateThunks = {
  animateImage,
  animateAiImage,
  instantImageAnimation
}
